/**
 * @fileoverview Hook for the admin-shaped legacy schema manifest (local-first + auto-populate).
 * @module system-web/hooks/useLegacySchemaManifest
 *
 * Responsibilities:
 * - Subscribe to the persisted schema manifest in Dexie via liveQuery.
 * - Automatically trigger populateFromSql() on first open when no manifest exists.
 * - Provide imperative actions: populateFromSql (refresh from SQL), toggleTableDisabled,
 *   addFk, removeFk, save — all of which POST to system-api and let sync pull the result.
 *
 * Dependencies:
 * - legacySchemaManifestService
 * - dexie/liveQuery
 *
 * Architecture:
 * - liveQuery observed function is a pure Dexie read (getSchemaManifest) — no writes.
 * - Writes (populate, toggle, addFk, removeFk, save) are imperative — called from handlers,
 *   never inside liveQuery.
 * - Local pending edits (disable toggles + FK additions not yet saved) are kept in local
 *   React state (pendingTables, pendingFks) so the UI responds immediately without a round-trip.
 *   On Save, the pending state is posted to the server; on discard, pending state is reset to
 *   the last saved manifest.
 *
 * Notes:
 * - populateFromSql is automatically triggered on mount when legacyDb is provided AND
 *   the Dexie manifest is null (no saved manifest exists yet).
 * - Subsequent refreshes require an explicit call to triggerPopulate().
 * - liveQuery observed function ONLY reads — NEVER writes to Dexie.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { liveQuery } from 'dexie';
import {
  getSchemaManifest,
  saveSchemaManifest,
  populateFromSql,
  type SchemaManifestDoc,
  type SchemaManifestTableEntry,
  type SchemaManifestFk,
} from '../services/legacySchemaManifestService';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract LegacySchemaManifestState
 *
 * Purpose:
 * - Shape returned by useLegacySchemaManifest.
 *
 * Fields:
 * - manifest: The last saved manifest from Dexie (null = no manifest yet).
 * - pendingTables: Local working copy of tables with admin disable flags (not yet saved).
 * - pendingFks: Local working copy of declared FKs (not yet saved).
 * - isDirty: True when pendingTables or pendingFks differ from the saved manifest.
 * - isLoading: True during the initial liveQuery setup or populateFromSql.
 * - isPopulating: True specifically while fetching tables from SQL and saving initial manifest.
 * - isSaving: True while a save POST is in-flight.
 * - error: Non-null when any operation failed.
 * - toggleTableDisabled: Toggle a table's disabled flag in pendingTables.
 * - toggleColumnDisabled: Toggle a column's disabled flag in pendingTables.
 * - setTableNotes: Update a table's notes field in pendingTables.
 * - addFk: Add a FK to pendingFks.
 * - removeFk: Remove a FK from pendingFks by index.
 * - save: POST pendingTables + pendingFks to server.
 * - discard: Reset pending state to the last saved manifest.
 * - triggerPopulate: Manually trigger populateFromSql (for the Refresh button).
 *
 * Rules:
 * - Never call mutations inside liveQuery.
 *
 * Sync:
 * - manifest comes from Dexie liveQuery (local-first).
 * - Mutations POST to server; manifest updates after next sync pull.
 */
export interface LegacySchemaManifestState {
  manifest: SchemaManifestDoc | null;
  pendingTables: SchemaManifestTableEntry[];
  pendingFks: SchemaManifestFk[];
  isDirty: boolean;
  isLoading: boolean;
  isPopulating: boolean;
  isSaving: boolean;
  error: string | null;
  toggleTableDisabled: (table: string) => void;
  toggleColumnDisabled: (table: string, column: string) => void;
  setTableNotes: (table: string, notes: string) => void;
  addFk: (fk: SchemaManifestFk) => void;
  removeFk: (index: number) => void;
  save: () => Promise<void>;
  discard: () => void;
  triggerPopulate: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @hook useLegacySchemaManifest
 *
 * Purpose:
 * - Provide the admin-shaped legacy schema manifest with auto-populate and local editing.
 *
 * Returns:
 * - See LegacySchemaManifestState.
 *
 * Side Effects:
 * - On mount (when legacyDb is provided): subscribes to Dexie liveQuery.
 * - When manifest is null after initial load: automatically calls populateFromSql().
 * - triggerPopulate(), save() call system-api (online mutations).
 *
 * Offline Behavior:
 * - manifest from Dexie is available offline.
 * - populateFromSql() and save() require connectivity.
 *
 * @errorHandling
 * Strategy:   All async errors are caught and stored in `error` state.
 * User Impact: SchemaManifestEditor shows an inline error banner.
 * Logging:    REQUIRED — log failures with legacyDb.
 */
export function useLegacySchemaManifest(legacyDb: string | null): LegacySchemaManifestState {
  const [manifest, setManifest] = useState<SchemaManifestDoc | null | undefined>(undefined);
  const [pendingTables, setPendingTables] = useState<SchemaManifestTableEntry[]>([]);
  const [pendingFks, setPendingFks] = useState<SchemaManifestFk[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPopulating, setIsPopulating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track whether we've already triggered the auto-populate for this legacyDb
  const autoPopulatedForRef = useRef<string | null>(null);

  // liveQuery subscription — pure read, no writes
  useEffect(() => {
    if (!legacyDb) {
      setManifest(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    const subscription = liveQuery(() => getSchemaManifest(legacyDb)).subscribe({
      next: (doc) => {
        setManifest(doc);
        setIsLoading(false);
      },
      error: (err) => {
        console.error('[useLegacySchemaManifest] liveQuery error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load schema manifest');
        setIsLoading(false);
      },
    });
    return () => subscription.unsubscribe();
  }, [legacyDb]);

  // Sync pending state when the saved manifest changes (e.g. after server save)
  useEffect(() => {
    if (manifest !== undefined) {
      setPendingTables(manifest?.tables ?? []);
      setPendingFks(manifest?.declaredFks ?? []);
      setIsDirty(false);
    }
  }, [manifest]);

  // Auto-populate from SQL when manifest is null (first open, no saved manifest yet)
  useEffect(() => {
    if (
      legacyDb &&
      manifest === null &&
      !isPopulating &&
      autoPopulatedForRef.current !== legacyDb
    ) {
      autoPopulatedForRef.current = legacyDb;
      void (async () => {
        setIsPopulating(true);
        setError(null);
        try {
          const populated = await populateFromSql(legacyDb, null);
          setManifest(populated);
          setPendingTables(populated.tables ?? []);
          setPendingFks(populated.declaredFks ?? []);
          setIsDirty(false);
        } catch (err) {
          console.error('[useLegacySchemaManifest] auto-populate failed:', err);
          setError(
            err instanceof Error
              ? err.message
              : 'Could not load tables from legacy database — check connection.',
          );
        } finally {
          setIsPopulating(false);
        }
      })();
    }
  }, [legacyDb, manifest, isPopulating]);

  // ---------------------------------------------------------------------------
  // Local pending edits
  // ---------------------------------------------------------------------------

  const toggleTableDisabled = useCallback((table: string) => {
    setPendingTables((prev) => prev.map((t) => (t.table === table ? { ...t, disabled: !t.disabled } : t)));
    setIsDirty(true);
  }, []);

  const toggleColumnDisabled = useCallback((table: string, column: string) => {
    setPendingTables((prev) => prev.map((t) => {
      if (t.table !== table) return t;
      const current = t.disabledColumns ?? [];
      const next = current.includes(column)
        ? current.filter((name) => name !== column)
        : [...current, column];
      return { ...t, disabledColumns: next };
    }));
    setIsDirty(true);
  }, []);

  const setTableNotes = useCallback((table: string, notes: string) => {
    setPendingTables((prev) => prev.map((t) => (t.table === table ? { ...t, notes: notes || undefined } : t)));
    setIsDirty(true);
  }, []);

  const addFk = useCallback((fk: SchemaManifestFk) => {
    setPendingFks((prev) => [...prev, fk]);
    setIsDirty(true);
  }, []);

  const removeFk = useCallback((index: number) => {
    setPendingFks((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  const save = useCallback(async () => {
    if (!legacyDb) return;
    setIsSaving(true);
    setError(null);
    try {
      const saved = await saveSchemaManifest(legacyDb, pendingTables, pendingFks);
      setManifest(saved);
      setPendingTables(saved.tables ?? []);
      setPendingFks(saved.declaredFks ?? []);
      setIsDirty(false);
    } catch (err) {
      console.error('[useLegacySchemaManifest] save failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to save schema manifest');
    } finally {
      setIsSaving(false);
    }
  }, [legacyDb, pendingTables, pendingFks]);

  // ---------------------------------------------------------------------------
  // Discard
  // ---------------------------------------------------------------------------

  const discard = useCallback(() => {
    setPendingTables(manifest?.tables ?? []);
    setPendingFks(manifest?.declaredFks ?? []);
    setIsDirty(false);
    setError(null);
  }, [manifest]);

  // ---------------------------------------------------------------------------
  // Manual re-populate from SQL (Refresh button)
  // ---------------------------------------------------------------------------

  const triggerPopulate = useCallback(async () => {
    if (!legacyDb) return;
    setIsPopulating(true);
    setError(null);
    try {
      if (isDirty) {
        await saveSchemaManifest(
          legacyDb,
          pendingTables,
          pendingFks.length > 0 ? pendingFks : (manifest?.declaredFks ?? []),
        );
      }
      const populated = await populateFromSql(legacyDb, manifest ?? null);
      setManifest(populated);
      setPendingTables(populated.tables ?? []);
      setPendingFks(populated.declaredFks ?? []);
      setIsDirty(false);
    } catch (err) {
      console.error('[useLegacySchemaManifest] triggerPopulate failed:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Could not refresh from legacy database — check connection.',
      );
    } finally {
      setIsPopulating(false);
    }
  }, [legacyDb, manifest, isDirty, pendingTables, pendingFks]);

  // ---------------------------------------------------------------------------
  // Dirty check
  // ---------------------------------------------------------------------------

  return {
    manifest: manifest ?? null,
    pendingTables,
    pendingFks,
    isDirty,
    isLoading: isLoading || manifest === undefined,
    isPopulating,
    isSaving,
    error,
    toggleTableDisabled,
    toggleColumnDisabled,
    setTableNotes,
    addFk,
    removeFk,
    save,
    discard,
    triggerPopulate,
  };
}
